/** leads/LeadAssignModal.tsx — 배정 모달 (이름·휴대폰 검색 우선, 미등록 FP 즉석 등록, 지점 배정) */
import React, { useMemo, useState } from 'react';
import { assignLeads } from './api';
import type { AssignRenewed, AssignUnchecked } from './api';
import { FEATURES } from './config';
import type { LeadMeta, MetaFp } from './types';

interface Props {
  leadIds: string[];
  unitCount?: number;   // 회사 보기에서 고른 «회사 수» (리드 수와 다를 수 있다 — 2026-09-18)
  unitLabel?: string;   // '개사' | '건'
  meta: LeadMeta | null;
  onClose: () => void;
  onDone: () => void;
  onToast: (m: string, t?: string) => void;
}

export default function LeadAssignModal({ leadIds, unitCount, unitLabel, meta, onClose, onDone, onToast }: Props) {
  const cnt = unitCount ?? leadIds.length;
  const unit = unitLabel || '건';
  const extra = unitCount != null && unitCount !== leadIds.length ? ` (납입연도 ${leadIds.length}건)` : '';
  const [q, setQ] = useState('');
  const [pickedFp, setPickedFp] = useState<MetaFp | null>(null);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [branch, setBranch] = useState('');
  const [busy, setBusy] = useState(false);
  // 배정 결과 — 실시간 갱신 확인으로 «배정하지 않은» 회사가 있으면 토스트 대신 결과 화면으로 관리자에게 알린다 (2026-09-19)
  const [result, setResult] = useState<{ msg: string; renewed: AssignRenewed[]; unchecked: AssignUnchecked[]; checked: number } | null>(null);
  const fmtD = (d: string | null | undefined) => (d ? d.replace(/-0?(\d+)-0?(\d+)$/, '.$1.$2') : '—');

  const digits = (s: string) => s.replace(/[^0-9]/g, '');

  const filtered = useMemo(() => {
    const fps: MetaFp[] = [
      ...(meta?.fps || []).filter((f) => (f.kind || 'fp') !== 'wm'), // WM 미러 제외 — 아래 wms 병합과 중복 방지 (2026-07-17)
      // WM도 같은 검색 리스트에 병합 — 이름 옆 [WM] 칩으로 구분 (사용자 확정: WM에게도 DB)
      ...((meta?.wms || []).map((w) => ({ id: w.id, name: w.name, phone: w.phone || '', company: w.company || '', cart_size: (w as { cart_size?: number }).cart_size ?? 10, active_count: (w as { active_count?: number }).active_count ?? 0, kind: 'wm' as const }))),
    ];
    const t = q.trim();
    if (!t) return fps.slice(0, 30);
    const dq = digits(t);
    return fps.filter((f) =>
      f.name.includes(t) || (dq.length >= 3 && digits(f.phone).includes(dq)) || (f.company || '').includes(t)
    ).slice(0, 30);
  }, [q, meta]);

  const noMatch = q.trim().length > 0 && filtered.length === 0;

  const submit = async (payload: { fp_id?: string; wm_id?: string; branch?: string; new_fp?: { name: string; phone: string } }) => {
    setBusy(true);
    try {
      const r = await assignLeads({ lead_ids: leadIds, ...payload });
      const renewed = r.renewed || []; const unchecked = r.unchecked || [];
      // 회사 단위(2026-10-21 지도사 「회사 단위로 카운트해」) — 납입연도 줄 수가 아니라 회사 수
      let msg = (r.companies != null ? r.companies + '곳' : r.updated + '건') + ' 배정 완료';
      if (r.fp) msg = r.fp.name + ' ' + (r.fp.kind === 'wm' ? 'WM' : 'FP') + '에게 ' + msg;
      if (r.rejected > 0) msg += ' · ' + r.rejected + '곳 장바구니 초과 미배정';
      if (r.cart) msg += ' (보유 ' + r.cart.used + '/' + r.cart.size + ')';
      if (unchecked.length > 0) msg += ' · 유효기간 확인 불가 ' + unchecked.length + '건은 그대로 배정';
      // 배정 → 카톡 전송이 한 동선이 되도록 포털 링크 자동 복사
      if (r.fp && r.fp.portal_url && navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(r.fp.short_url || r.fp.full_url || window.location.origin + r.fp.portal_url);
          msg += ' · 포털 링크(단축) 복사됨';
        } catch (ce) { /* 클립보드 권한 없으면 복사 생략 */ }
      }
      if (renewed.length > 0) { setResult({ msg: r.updated > 0 ? msg : '배정된 건이 없습니다', renewed, unchecked, checked: r.checked || 0 }); return; } // 갱신 건은 결과 화면으로 (관리자 알림)
      onToast(msg, r.rejected > 0 ? 'error' : undefined);
      onDone();
    } catch (e) { onToast((e as Error).message, 'error'); } finally { setBusy(false); }
  };

  const submitNewFp = () => {
    if (!newName.trim()) { onToast('FP 이름을 입력하세요', 'error'); return; }
    const d = digits(newPhone);
    if (d.length < 10 || d.length > 11) { onToast('휴대폰 번호는 10~11자리로 입력하세요', 'error'); return; }
    submit({ new_fp: { name: newName.trim(), phone: d } });
  };

  if (result) {
    return (
      <div className="ld-modal-bg ld-root" onClick={onDone}>
        <div className="ld-modal" style={{ width: 'min(560px, 100%)' }} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="갱신 확인 결과">
          <div className="ld-modal-h">
            <span>갱신 확인 — 배정하지 않은 회사 {result.renewed.length}건</span>
            <button className="ld-btn" onClick={onDone}>닫기</button>
          </div>
          <div className="ld-modal-b">
            <div className="ld-gate-note">배정 직전 <b>중소벤처24 증명서 API</b>로 유효기간을 실시간 확인했습니다. 아래 회사는 <b>이미 재인증(갱신)</b>되어 있어 배정하지 않고 종결 처리했으며, 만료예정 목록에도 새 유효기간이 반영됩니다.</div>
            <table className="ld-gate-tbl">
              <thead><tr><th>회사</th><th>인증</th><th>알던 만료일</th><th>새 유효기간</th></tr></thead>
              <tbody>
                {result.renewed.map((x) => (
                  <tr key={x.lead_id}><td><b>{x.corp_name}</b></td><td>{x.kind_label}</td><td className="mut">{fmtD(x.old_end)}</td><td className="new">~ {fmtD(x.new_end)} <span className="chip">갱신됨</span></td></tr>
                ))}
              </tbody>
            </table>
            <div className="ld-gate-sum">{result.msg}{result.checked ? ` · 실시간 확인 ${result.checked}건` : ''}</div>
            {result.unchecked.length > 0 && (
              <div className="ld-gate-un">확인 불가(그대로 배정) {result.unchecked.length}건 — {result.unchecked.slice(0, 4).map((u) => `${u.corp_name}(${u.reason})`).join(' · ')}{result.unchecked.length > 4 ? ' …' : ''}</div>
            )}
            <button className="ld-btn ok" style={{ width: '100%', marginTop: 12 }} onClick={onDone}>확인</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ld-modal-bg ld-root" onClick={onClose}>
      <div className="ld-modal" style={{ width: 'min(560px, 100%)' }} onClick={(e) => e.stopPropagation()}>
        <div className="ld-modal-h">
          <span>DB 배정 — 선택 {cnt}{unit}{extra}</span>
          <button className="ld-btn" onClick={onClose}>닫기</button>
        </div>
        <div className="ld-modal-b">
          <div className="ld-field">
            <label>FP 검색 (이름 또는 휴대폰)</label>
            <input autoFocus value={q} onChange={(e) => { setQ(e.target.value); setPickedFp(null); }} placeholder="홍길동 / 01012345678" />
          </div>

          <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 10 }}>
            {filtered.map((f) => {
              const full = f.active_count >= f.cart_size;
              const on = pickedFp?.id === f.id;
              return (
                <div key={f.id} onClick={() => !full && setPickedFp(f)}
                  style={{ padding: '9px 12px', borderBottom: '1px solid #F3F4F6', cursor: full ? 'not-allowed' : 'pointer',
                    background: on ? '#EFF6FF' : undefined, opacity: full ? 0.5 : 1,
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }} title={f.name + ' · ' + (f.phone || '') + (f.company ? ' · ' + f.company : '')}><b>{f.name}</b>{f.kind === 'wm' && <span style={{ marginLeft: 5, fontSize: 10.5, fontWeight: 700, color: '#7C3AED', background: '#F5F3FF', padding: '1px 6px', borderRadius: 99 }}>WM</span>} · {f.phone || '번호없음'}{f.company ? ' · ' + f.company : ''}</span>
                  <span className="ld-year" style={full ? { background: '#FEF2F2', color: '#DC2626' } : {}}>
                    {f.active_count}/{f.cart_size}{full ? ' 가득참' : ''}
                  </span>
                </div>
              );
            })}
            {noMatch && <div style={{ padding: 14, fontSize: 13, color: '#4B5563' }}>일치하는 FP가 없습니다 — 아래에서 새로 등록하며 배정하세요.</div>}
            {!q.trim() && filtered.length === 0 && <div style={{ padding: 14, fontSize: 13, color: '#9CA3AF' }}>등록된 FP가 없습니다</div>}
          </div>

          {pickedFp && (
            <button className="ld-btn ok" style={{ width: '100%', marginTop: 10 }} disabled={busy}
              onClick={() => submit(pickedFp.kind === 'wm' ? { wm_id: pickedFp.id } : { fp_id: pickedFp.id })}>
              {busy ? '배정 중 — 인증 만료 DB는 유효기간을 실시간 확인합니다…' : `${pickedFp.name} (${pickedFp.phone}) ${pickedFp.kind === 'wm' ? 'WM' : 'FP'}에게 ${cnt}${unit} 배정${extra}`}
            </button>
          )}

          <div style={{ margin: '16px 0 8px', fontSize: 12, fontWeight: 700, color: '#4B5563' }}>미등록 FP 즉석 등록 + 배정</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="FP 이름"
              style={{ flex: 1, padding: '8px 10px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13 }} />
            <input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="휴대폰 번호"
              style={{ flex: 1.4, padding: '8px 10px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13 }} />
            <button className="ld-btn pri" disabled={busy} onClick={submitNewFp}>등록+배정</button>
          </div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>휴대폰 번호가 이미 등록돼 있으면 기존 FP에게 배정됩니다 (전 모듈 공유).</div>

          {FEATURES.branchAssign && (<>
          <div style={{ margin: '16px 0 8px', fontSize: 12, fontWeight: 700, color: '#4B5563' }}>지점 배정 (선점제)</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <select value={branch} onChange={(e) => setBranch(e.target.value)}
              style={{ flex: 1, padding: '8px 10px', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13 }}>
              <option value="">지점 선택…</option>
              {(meta?.branches || []).map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
            <button className="ld-btn" disabled={busy || !branch} onClick={() => submit({ branch })}>지점 배정</button>
          </div>
          </>)}
        </div>
      </div>
    </div>
  );
}
